import React, { useState, useEffect} from 'react';
import {Grid, Checkbox, Typography, Dialog, DialogContent, DialogContentText, DialogActions, Button, Box, Link} from '@mui/material';
import { useRequestContext } from '../../../Request.Context';
import { useTranslation } from 'react-i18next';
import { useRequestdata } from '../../../useRequestData';
import DocumentDniRow from '../StudentDataStep/DocumentDniRow/DocumentDniRow';
import DocumentRow from '../AcademicHistoryStep/DocumentRow/DocumentRow';
import AppFile from '../../../../../models/AppFile';
import { useAppSelector } from '../../../../../shared/redux/store';
import API from '../../../../../api/Api';
import useRuleValidation from '../../../../../shared/hooks/useRuleValidation';
import InfoDialog from '../../../../../components/InfoDialog/InfoDialog';
import { IframeContent } from '../../../../../components/IframeContent/IframeContent';
import { RuleDniValidationInterface } from '../../../../../shared/hooks/useDniValidation';
import { useRequestType } from '../../../../../shared/hooks/useRequestType';
import CheckIcon from '@mui/icons-material/Check';

const FormContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Grid container spacing={3} style={{ paddingLeft: '60px', paddingRight: '60px' }}>
    {children}
  </Grid>
);

const DocumentUploadStep = ({ requestData }: any) => {
  const { t } = useTranslation();
  const { studentRecord } = requestData;

  const {
    academicReport,
    setAcademicReport,
    syllabusFile,
    setSyllabusFile,
    termsAndConditionsChecked,
    setTermsAndConditionsChecked,
  } = useRequestdata({});

  const requestContext = useRequestContext();
  const {
    duFront,
    duBack,
    setDuFront,
    setDuBack,
    setDuFrontValidated,
    setDuBackValidated,
    setDuFrontValidationResult,
    setDuBackValidationResult,
    setDuFrontFileName,
    setDuBackFileName,
  } = requestContext;

  const { isInternal } = useRequestType();
  const { generalFilesSize } = useAppSelector((state) => state.settings);
  const state = useAppSelector((state) => state);
  const flagTrafficLight = state.settings.rekognitionStatus;

  const [isDniFrontLoading, setIsDniFrontLoading] = useState(false);
  const [isDniBackLoading, setIsDniBackLoading] = useState(false);

  
  const [openTutorialDialog, setOpenTutorialDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [secondCheck, setSecondCheck] = useState(false);
  
const [dniFrontValidation, setDniFrontValidation] = useState<null | RuleDniValidationInterface>(null);
  const [dniBackValidation, setDniBackValidation] = useState<null | RuleDniValidationInterface>(null);

  const [duFrontErrorMessage, setDuFrontErrorMessage] = useState<string | null>(null);
  const [duBackErrorMessage, setDuBackErrorMessage] = useState<string | null>(null);

  const academicReportRule = useRuleValidation(() => !!academicReport);
  const syllabusFileRule = useRuleValidation(() => !!syllabusFile);

  const checkRuleValidation = (rule: boolean) => (studentRecord && isInternal ? true : rule);
  const duFrontRule = useRuleValidation(() => checkRuleValidation(!!duFront));
  const duBackRule = useRuleValidation(() => checkRuleValidation(!!duBack));


  const disableAcademicUpload = isInternal;

  const handleOpen = () => {
    setOpen(true)
    setSecondCheck(termsAndConditionsChecked)
  };
  const handleClose = () => setOpen(false);

  const handleAcceptTermsAndConditions = () => {
    setOpen(false);
    setTermsAndConditionsChecked(true);
  };

  const convertFileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = (reader.result as string).replace(/^data:image\/[a-z]+;base64,/, '');
        resolve(base64String);
      };
      reader.onerror = () => reject(new Error('Error to read file'));
      reader.readAsDataURL(file);
    });
  };

  const extractBase64FromFile = async (fileObject: AppFile): Promise<string | null> => {
    if (fileObject.file) {
      return convertFileToBase64(fileObject.file);
    }
    return null;
  };
  useEffect(() => {
    if (requestContext.duFrontValidationResult) {
      setDniFrontValidation(requestContext.duFrontValidationResult);
    }

    if (requestContext.duBackValidationResult) {
      setDniBackValidation(requestContext.duBackValidationResult);
    }
  }, [requestContext.duFrontValidationResult, requestContext.duBackValidationResult]);

  const validateDocument = async (file: AppFile | null, documentNumber: string) => {
    if (!file) return null;
    try {
      const validationResult = await validateDni(file, documentNumber);
      return validationResult;
    } catch (error) {
      console.error('Error validating document:', error);
      return null;
    }
  };

  const validateDni = async (file: AppFile, personalId: string) => {
    const settings = state.settings;
    const urlSettings = settings.rekognitionUrl;
    if (!file || flagTrafficLight < 0) return null;

    try {
      const base64Image = await extractBase64FromFile(file);
      if (base64Image) {
        try {
          const result = await API.postDniValidation(base64Image, personalId, urlSettings);
          if (result) {
            if (file?.file_type === 'DU_FRONT') {
              setDuFrontErrorMessage(null);
            } else if (file?.file_type === 'DU_BACK') {
              setDuBackErrorMessage(null);
            }
            return {
              valid: result.isIdCard?.status && result.isText?.status && result.personalIdIsPresent?.status,
              isLoading: false,
              requiredText: result.isIdCard.message,
              requiredTextStatus: result.isIdCard.status,
              requiredTextLine2: result.isText.message,
              requiredTextLine2Status: result.isText.status,
              requiredTextLine3: result.personalIdIsPresent.message,
              requiredTextLine3Status: result.personalIdIsPresent.status,
              trafficLight: result.isIdCard?.status && result.isText?.status && result.personalIdIsPresent?.status ? 3 : (result.isIdCard?.status || result.isText?.status || result.personalIdIsPresent?.status ? 2 : 1),
              fileName: file.file_name,
              fileType: file.file_type
            };
          } else {
            if (file?.file_type === 'DU_FRONT') {
              setDuFrontErrorMessage(t('fileFormatError'));
            } else if (file?.file_type === 'DU_BACK') {
              setDuBackErrorMessage(t('fileFormatError'));
            }
          }
        } catch (e) {
          console.error(e)
          if (file?.file_type === 'DU_FRONT') {
            setDuFrontErrorMessage(t('fileFormatError'));
          } else if (file?.file_type === 'DU_BACK') {
            setDuBackErrorMessage(t('fileFormatError'));
          }
        }
      }
    } catch (error) {
      console.error(error);
      return {
        valid: false,
        isLoading: false,
        requiredText: '',
        requiredTextStatus: false,
        requiredTextLine2: '',
        requiredTextLine2Status: false,
        requiredTextLine3: '',
        requiredTextLine3Status: false,
        trafficLight: 0,
        fileName: file.file_name,
        fileType: file.file_type
      };
    }
    return null;
  };

  const setDuFrontObject = (file: AppFile | null) => {
    if (file && file.file_name !== duFront?.file_name) {
      setDuFront(file);
      setDuFrontFileName(file.file_name);
      setDuFrontValidated(false);
      setIsDniFrontLoading(true);
      validateDni(file, requestData?.student?.document_number || state.user.profile?.documentNumber)
        .then((validationResult) => {
          setDniFrontValidation(validationResult);
          requestContext.setDuFrontValidated(true);
          setDuFrontValidationResult(validationResult);
          requestContext.setDuFrontValidationResult(validationResult);
          setDuFrontValidated(true);
        })
        .finally(() => setIsDniFrontLoading(false));
    }
  };

  const setDuBackObject = (file: AppFile | null) => {
    if (file && file.file_name !== duBack?.file_name) {
      setDuBack(file);
      setDuBackFileName(file.file_name);
      setDuBackValidated(false);
      setIsDniBackLoading(true);
      validateDni(file, requestData?.student?.document_number || state.user.profile?.documentNumber)
        .then((validationResult) => {
          setDniBackValidation(validationResult);
          requestContext.setDuBackValidated(true);
          setDuBackValidationResult(validationResult);
          requestContext.setDuBackValidationResult(validationResult);
          setDuBackValidated(true);
        })
        .finally(() => setIsDniBackLoading(false));
    }
  };

  return (
    <FormContainer>
      <Box
        sx={{
          width: '100%',
          border: '1px solid #D9D9D9',
          borderRadius: '12px',
          padding: '24px',
          marginTop: '50px',
          backgroundColor: '#fff',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 400, color: '#006c5b', fontSize: '20px', marginRight: '10px', marginBottom:'10px' }}>
              {t('personalDocumentation')}
            </Typography>
        <Grid container spacing={3}>

          <Grid item xs={12} sm={6}>
            {isInternal && duFrontRule ? (
              <Box
                sx={{
                  border: '#a7e7a7',
                  backgroundColor:  '#DDFFDD',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography fontWeight={500}>
                    {t('du_front')}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t('documentUploaded')}
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 30, color: '#006c5b87' }}/>
              </Box>
            ) : (
              <DocumentDniRow
                fileSize={generalFilesSize}
                defaultFile={duFront}
                fileName={duFront?.file_name}
                setFile={setDuFrontObject}
                label={t('duFront')}
                documentType="DU_FRONT"
                buttonLabel={t('selectImage')}
                allowedExtensions={['jpg', 'png']}
                isLoading={isDniFrontLoading}
                dniRule={dniFrontValidation}
                flagTrafficLight={flagTrafficLight}
                adornment={duFrontRule.adornment}
                errorMessage={duFrontErrorMessage}
              />
            )}
            
          </Grid>

          <Grid item xs={12} sm={6}>
            {isInternal && duBackRule ? (
              <Box
                sx={{
                  border: '#a7e7a7',
                  backgroundColor:  '#DDFFDD',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography fontWeight={500}>
                    {t('du_back')}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t('documentUploaded')}
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 30, color: '#006c5b87' }}/>
              </Box>
            ) : (
              <DocumentDniRow
                fileSize={generalFilesSize}
                defaultFile={duBack}
                fileName={duBack?.file_name}
                setFile={setDuBackObject}
                label={t('duBack')}
                documentType="DU_BACK"
                buttonLabel={t('selectImage')}
                allowedExtensions={['jpg', 'png']}
                isLoading={isDniBackLoading}
                dniRule={dniBackValidation}
                flagTrafficLight={flagTrafficLight}
                adornment={duBackRule.adornment}
                errorMessage={duBackErrorMessage}
              />
            )}
            
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          width: '100%',
          border: '1px solid #D9D9D9',
          borderRadius: '12px',
          padding: '24px',
          marginTop: '40px',
          backgroundColor: '#fff',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 400, color: '#006c5b', fontSize: '20px', marginRight: '10px', marginBottom:'10px' }}>
          {t('academicDocumentation')}
        </Typography>
      
          <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            {disableAcademicUpload ? (
              <Box
                sx={{
                  border: '#DDFFDD',
                  backgroundColor:  '#DDFFDD',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography fontWeight={500}>
                    {t('academicReport')}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t('documentUploaded')}
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 30, color: '#006c5b87' }}/>
              </Box>
            ) : (
              <DocumentRow
                fileSize={generalFilesSize}
                defaultFile={academicReport}
                fileName={academicReport?.file_name}
                setFile={setAcademicReport}
                label={t('academicReport')}
                documentType="ACADEMIC_REPORT"
                validation={academicReportRule.adornment}
                buttonLabel={t('PDF')}
                allowedExtensions={['pdf']}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {disableAcademicUpload ? (
              <Box
                sx={{
                  border: '#a7e7a7',
                  backgroundColor:  '#DDFFDD',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography fontWeight={500}>
                    {t('syllabusFile')}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t('documentUploaded')}
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 30, color: '#006c5b87' }}/>
              </Box>
            ) : (
              <DocumentRow
                fileSize={generalFilesSize}
                defaultFile={syllabusFile}
                fileName={syllabusFile?.file_name}
                setFile={setSyllabusFile}
                label={t('syllabusFile')}
                documentType="SYLLABUS"
                validation={syllabusFileRule.adornment}
                buttonLabel={t('PDF')}
                allowedExtensions={['pdf']}
              />
            )}
          </Grid>
        </Grid>
      {!disableAcademicUpload && (
        <Button onClick={() => setOpenTutorialDialog(true)} sx={{ mt: 3 }}>
          {t('documentationHelpButton')}
        </Button>
      )}

        <InfoDialog
          open={openTutorialDialog}
          title={t('infotitle')}
          content={<IframeContent />}
          onClose={() => setOpenTutorialDialog(false)}
          size="lg"
        />
      </Box>
      {/* terminos */}
      <Grid item xs={12}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '20px', justifyContent: 'center' }}>
          <Checkbox
            checked={termsAndConditionsChecked}
            value={termsAndConditionsChecked}
            onClick={handleOpen}
          />
          <div style={{ marginLeft: '10px', paddingTop: '4px' }}>
            <Typography fontSize="1.05rem">
              {t('acceptTermsAndConditions')}
              <Button
              variant='text'
              onClick={handleOpen}
              sx={{ textTransform: 'none', color: '#479184', textDecoration: 'underline', minWidth: 'auto', p: 0 }}
            >
              {t('termsAndConditions')}
            </Button>
            </Typography>
          </div>
        </div>

        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          maxWidth="md"
        >
          <DialogContent>
            <DialogContentText style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'black', paddingBottom: '20px' }}>
              <Typography fontSize="24px">{t('generalConditions')}</Typography>
            </DialogContentText>
            <div style={{ overflowY: 'auto', maxHeight: '70vh' }}>
              <DialogContentText style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'black' }}>
                <Typography fontSize="20px">{'1. ' + t('termsAndConditions1title')}</Typography>
                <Typography>{t('termsAndConditions1paragraph1')}</Typography>
                <Typography>{t('termsAndConditions1paragraph2')}</Typography>
                <Typography>{t('termsAndConditions1paragraph3')}</Typography>
                <Typography fontSize="20px">{'1.1. ' + t('termsAndConditions11title')}</Typography>
                <Typography>{t('termsAndConditions11paragraph1')}</Typography>
                <Typography>{t('termsAndConditions11paragraph2')}</Typography>
                <Typography>{t('termsAndConditions11paragraph3')}</Typography>
                <Typography>{t('termsAndConditions11paragraph4')}</Typography>
                <Typography fontSize="20px">{'1.2. ' + t('termsAndConditions12title')}</Typography>
                <Typography>{t('termsAndConditions12paragraph1')}</Typography>
                <Typography>{t('termsAndConditions12paragraph2')}</Typography>
                <Typography>{t('termsAndConditions12paragraph3')}</Typography>
                <Typography>
                  <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
                    {t('termsAndConditions12paragraph4underlined')}
                  </span>{' '}
                  {t('termsAndConditions12paragraph4')}
                </Typography>
              </DialogContentText>
              <div style={{ display: 'flex', alignItems: 'center', padding: '20px', justifyContent: 'center' }}>
                <Checkbox
                  checked={secondCheck}
                  value={termsAndConditionsChecked}
                  onClick={() => setSecondCheck(!secondCheck)}
                  id="readAndAcceptTermsAndConditions"
                />
                <div style={{ marginLeft: '10px', paddingTop: '4px' }}>
                  <Typography fontSize="1.05rem">
                    {t('readAndAcceptTermsAndConditions')}
                    <u style={{ color: '#479184' }}>{t('termsAndConditions')}</u>
                  </Typography>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary" data-testid="closeTermAndCondition">{t('closeLabel')}</Button>
            <Button onClick={handleAcceptTermsAndConditions} color="primary" disabled={!secondCheck} data-testid="acceptTermAndCondition">{t('acceptLabel')}</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </FormContainer>
  );
};

export default DocumentUploadStep;
